import { ethers } from 'ethers';
import { BEP20_ABI } from '../constants/abis';
import { getProvider } from './chainUtils';

// Transaction queue implementation
class TransactionQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
  }

  addTransaction(transaction) {
    this.queue.push(transaction);
    if (!this.processing) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;
    while (this.queue.length > 0) {
      const tx = this.queue[0];
      try {
        await this.processTransaction(tx);
        this.queue.shift(); // Remove processed transaction
      } catch (error) {
        console.error('Transaction processing error:', error);
        if (!tx.retryCount || tx.retryCount < 3) {
          // Retry failed transaction
          tx.retryCount = (tx.retryCount || 0) + 1;
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5s before retry
          continue;
        }
        this.queue.shift(); // Remove failed transaction after 3 retries
      }
    }
    this.processing = false;
  }

  async processTransaction(tx) {
    const { type, params, onSuccess, onError } = tx;
    try {
      let result;
      switch (type) {
        case 'BNB_TRANSFER':
          result = await this.processBNBTransfer(params);
          break;
        case 'TOKEN_TRANSFER':
          result = await this.processTokenTransfer(params);
          break;
        default:
          throw new Error('Unsupported transaction type');
      }
      if (onSuccess) onSuccess(result);
      return result;
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  async processBNBTransfer(params) {
    const { from, to, amount, wallet, isTestnet } = params;
    const provider = getProvider('BNB', isTestnet);
    
    try {
      // Enhanced gas estimation
      const gasPrice = await provider.getGasPrice();
      const gasLimit = await provider.estimateGas({
        to,
        value: ethers.utils.parseEther(amount.toString())
      });

      // Add 20% buffer to gas limit
      const bufferedGasLimit = gasLimit.mul(12).div(10);

      // Create and send transaction
      const tx = await wallet.sendTransaction({
        to,
        value: ethers.utils.parseEther(amount.toString()),
        gasPrice,
        gasLimit: bufferedGasLimit
      });

      // Enhanced receipt validation
      const receipt = await this.validateTransactionReceipt(tx.hash, provider);
      return { tx, receipt };
    } catch (error) {
      throw this.enhanceTransactionError(error);
    }
  }

  async processTokenTransfer(params) {
    const { tokenAddress, from, to, amount, wallet, decimals, isTestnet } = params;
    const provider = getProvider('BNB', isTestnet);
    
    try {
      const contract = new ethers.Contract(tokenAddress, BEP20_ABI, wallet);
      
      // Check allowance for tokens if needed
      const allowance = await contract.allowance(from, to);
      if (allowance.lt(ethers.utils.parseUnits(amount.toString(), decimals))) {
        throw new Error('Insufficient token allowance');
      }

      // Enhanced gas estimation for token transfer
      const gasEstimate = await contract.estimateGas.transfer(
        to,
        ethers.utils.parseUnits(amount.toString(), decimals)
      );

      // Add 30% buffer for token transfers (they're more complex)
      const bufferedGasLimit = gasEstimate.mul(13).div(10);

      // Send token transfer
      const tx = await contract.transfer(
        to,
        ethers.utils.parseUnits(amount.toString(), decimals),
        { gasLimit: bufferedGasLimit }
      );

      // Enhanced receipt validation
      const receipt = await this.validateTransactionReceipt(tx.hash, provider);
      return { tx, receipt };
    } catch (error) {
      throw this.enhanceTransactionError(error);
    }
  }

  async validateTransactionReceipt(txHash, provider, maxAttempts = 5) {
    let attempts = 0;
    while (attempts < maxAttempts) {
      try {
        const receipt = await provider.getTransactionReceipt(txHash);
        if (!receipt) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        // Enhanced receipt validation
        if (!receipt.status) {
          throw new Error('Transaction failed on blockchain');
        }

        // Check confirmation count
        if (receipt.confirmations < 1) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        // Validate gas used vs gas limit
        if (receipt.gasUsed.mul(100).div(receipt.gasLimit).gt(90)) {
          console.warn('High gas usage detected:', receipt.gasUsed.toString());
        }

        // Check for contract creation
        if (receipt.contractAddress) {
          const code = await provider.getCode(receipt.contractAddress);
          if (code === '0x') {
            throw new Error('Contract creation failed');
          }
        }

        // For token transfers, verify events
        const logs = receipt.logs || [];
        const transferEvent = logs.find(log => 
          log.topics[0] === ethers.utils.id('Transfer(address,address,uint256)')
        );

        if (logs.length > 0 && !transferEvent) {
          console.warn('No Transfer event found in token transaction');
        }

        return receipt;
      } catch (error) {
        if (attempts >= maxAttempts - 1) throw error;
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    throw new Error('Transaction receipt validation failed');
  }

  enhanceTransactionError(error) {
    let enhancedError = new Error('Transaction failed');
    enhancedError.originalError = error;

    // Enhance common error messages
    if (error.code === 'INSUFFICIENT_FUNDS') {
      enhancedError.message = 'Insufficient funds for transaction. Please check your balance includes enough for gas fees.';
      enhancedError.code = 'INSUFFICIENT_FUNDS';
    } else if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
      enhancedError.message = 'Unable to estimate gas. The transaction may fail or the contract may have errors.';
      enhancedError.code = 'GAS_ESTIMATE_FAILED';
    } else if (error.message.includes('user rejected')) {
      enhancedError.message = 'Transaction was rejected by the user.';
      enhancedError.code = 'USER_REJECTED';
    } else if (error.message.includes('nonce')) {
      enhancedError.message = 'Transaction nonce error. Please try again.';
      enhancedError.code = 'NONCE_ERROR';
    } else if (error.message.includes('replacement fee too low')) {
      enhancedError.message = 'Gas price too low for replacement transaction.';
      enhancedError.code = 'LOW_GAS_PRICE';
    }

    return enhancedError;
  }
}

// Create singleton instance
export const transactionQueue = new TransactionQueue();

// Helper function to add transaction to queue
export const queueTransaction = (type, params, onSuccess, onError) => {
  transactionQueue.addTransaction({ type, params, onSuccess, onError });
};

// Export status constants
export const TransactionStatus = {
  QUEUED: 'QUEUED',
  PROCESSING: 'PROCESSING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  RETRY: 'RETRY'
}; 